import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { getDefaultValues } from 'components/modals/common/JobNameAssignmentModal/model'
import { getSchema } from 'components/modals/common/JobNameAssignmentModal/schema'
import {
  useConfirmationModal,
  useControlledForm,
  useFormApiError,
  useFormReset,
} from 'hooks'

const useNameAssignmentFormState = handlers => {
  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)

  const { onConfirm } = handlers

  const defaultValues = getDefaultValues()
  const schema = getSchema()

  const handleFormData = React.useCallback(
    data => {
      const { title } = data

      try {
        onConfirm({ title }, { onFailureAction: setApiError })
        setShouldReset(true)
      } finally {
        setShouldReset(false)
      }
    },
    [onConfirm],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    watch,
    isDirtyOrInvalid,
  } = useControlledForm({
    defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset({ shouldReset }, handleFormReset)

  return {
    state: { nameAssignment: { control, formState, isDirtyOrInvalid } },
    handlers: { handleFormReset, handleFormSubmit, watch },
  }
}

export const useTemplateBasedJobPositionModal = handlers => {
  const { onBack, onCancel, onClose, onConfirm } = handlers

  const {
    state: { nameAssignment },
    handlers: { handleFormReset, handleFormSubmit, watch },
  } = useNameAssignmentFormState({ onConfirm })

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    onClose()
    handleFormReset()
  })

  const { isDirtyOrInvalid } = nameAssignment

  const handleBack = React.useCallback(() => {
    if (onBack) {
      handleFormReset()
      onBack()
    }
  }, [handleFormReset, onBack])

  const handleCancel = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onCancel()
  }, [handleConfirmationOpen, onCancel, isDirtyOrInvalid])

  const handleClose = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onClose()
  }, [handleConfirmationOpen, onClose, isDirtyOrInvalid])

  const handleConfirm = React.useCallback(handleFormSubmit, [handleFormSubmit])

  return {
    state: {
      nameAssignment: { ...nameAssignment, watch },
      isConfirmationOpen,
    },
    handlers: {
      handleBack: onBack ? handleBack : null,
      handleCancel,
      handleClose,
      handleConfirm,
      handleConfirmationClose,
      handleConfirmationConfirm,
    },
  }
}
